import type { WorkflowStepPayload } from "./contract";
import { workflowReplyInput, type WorkflowReplyInput, type WorkflowValueIssue } from "./step";

/**
 * Copy of the workflow cards. The package stays i18n-free: hosts pass a (partial) set built from
 * their own catalogs; these English defaults keep the components usable on their own.
 */
export interface WorkflowLabels {
  /** Accessible name of the group of reply buttons. */
  choose: string;
  /** Marks the button the visitor picked (icon + text, never colour alone). */
  chosen: string;
  submit: string;
  skip: string;
  select: string;
  required: string;
  invalidEmail: string;
  invalidPhone: string;
  invalidNumber: string;
  invalidDate: string;
  invalidOption: string;
  /** The server re-sent the card because it could not use the value. */
  invalidFromServer: string;
  expired: string;
  /** Link to the full privacy notice under the consent line of a card that asks for personal data. */
  privacyLink: string;
  answeredWith: (value: string) => string;
  /** Composer placeholder / hint while an open card does not allow typing. */
  typingLocked: string;
}

/** Copy of the rating card (`ask_rating`). Kept apart from `WorkflowLabels` so hosts that translate only the other cards still compile; missing entries fall back to English. */
export interface WorkflowRatingLabels {
  /** Accessible name of the group of stars / thumbs. */
  ratingGroup: string;
  /** Accessible name of one star, e.g. "Rate 4 out of 5". */
  rateStar: (stars: number) => string;
  /** The stars as a phrase, e.g. "4 out of 5". */
  starsOutOf: (stars: number) => string;
  thumbsUp: string;
  thumbsDown: string;
  /** Label of the optional comment box. */
  ratingComment: string;
  /** Counter under the comment box, e.g. "120 of 500 characters". */
  ratingCommentCount: (used: number, max: number) => string;
  ratingSubmit: string;
  /** Polite announcement of the rating picked but not sent yet. */
  ratingSelected: (rating: string) => string;
}

/**
 * Copy of the reply composer (`collect_reply`). Kept apart like the rating labels so hosts that do not
 * translate it yet still compile; missing entries fall back to English.
 */
export interface WorkflowReplyLabels {
  /** Accessible name of the composer while it answers a question, e.g. "Answer: What is your student number?". */
  replyAnswer: (question: string) => string;
  /** Placeholder of the composer while it answers a question. */
  replyPlaceholder: string;
}

/**
 * Copy of the code card (`verify_email`). Kept apart like the reply labels so hosts that do not translate it yet still
 * compile; missing entries fall back to English. The button labels themselves come from the server (translated like any
 * button); `sendCode` / `resendCode` only stand in when a card arrives without them.
 */
export interface WorkflowVerifyLabels {
  /** Accessible name of the panel, e.g. "Verify your e-mail". */
  verifyGroup: string;
  sendCode: string;
  resendCode: string;
  /** Before the code is sent: where it will go. `email` is masked ("l•••@example.com"). */
  codeWillGoTo: (email: string) => string;
  /** After: where it went. */
  codeSentTo: (email: string) => string;
  /** When the code stops working, as a local time ("9:10 AM"). */
  codeExpiresAt: (time: string) => string;
  /** Wrong codes still allowed. */
  triesLeft: (count: number) => string;
  /** Why "Resend code" is not open yet. */
  resendIn: (seconds: number) => string;
  /** Polite announcement when "Resend code" opens. */
  resendReady: string;
  /** Accessible name of the bot check under the button. */
  securityCheck: string;
  /** The bot check has not finished yet. */
  securityCheckWait: string;
  /** What the composer says when the text typed for a code is not the digits the card asks for. */
  invalidCode: (length: number) => string;
}

/** Copy of the article card (`show_article`). The button's words come from the server (translated like any button); `DEFAULT_ARTICLE_LABEL` only stands in when a card arrives without them. */
export interface WorkflowArticleLabels {
  /** Accessible name of the card, e.g. "Article: Tuition plans 2027". */
  articleGroup: (title: string) => string;
  /** Read after the button's words: the link opens another tab. */
  articleNewTab: string;
}

/** What the button says when a card arrives without a label. */
export const DEFAULT_ARTICLE_LABEL = "Open article";

/** Every label a workflow card or the reply composer can use; the reply, code and article labels may be left out. */
export type WorkflowCardLabels = WorkflowLabels & WorkflowRatingLabels & Partial<WorkflowReplyLabels> & Partial<WorkflowVerifyLabels> & Partial<WorkflowArticleLabels>;

/** Every label, resolved (the defaults fill what the host left out). */
export type ResolvedWorkflowLabels = WorkflowLabels & WorkflowRatingLabels & WorkflowReplyLabels & WorkflowVerifyLabels & WorkflowArticleLabels;

export const DEFAULT_WORKFLOW_REPLY_LABELS: WorkflowReplyLabels = {
  replyAnswer: (question) => `Answer: ${question}`,
  replyPlaceholder: "Type your answer",
};

export const DEFAULT_WORKFLOW_VERIFY_LABELS: WorkflowVerifyLabels = {
  verifyGroup: "Verify your e-mail",
  sendCode: "Send code",
  resendCode: "Resend code",
  codeWillGoTo: (email) => `We'll e-mail a 6-digit code to ${email}.`,
  codeSentTo: (email) => `We sent a code to ${email}. Type it in the message box.`,
  codeExpiresAt: (time) => `The code works until ${time}.`,
  triesLeft: (count) => (count === 1 ? "1 try left" : `${String(count)} tries left`),
  resendIn: (seconds) => `You can ask for a new code in ${String(seconds)} s.`,
  resendReady: "You can ask for a new code now.",
  securityCheck: "Security check",
  securityCheckWait: "Wait for the security check to finish, then try again.",
  invalidCode: (length) => `Enter the ${String(length)}-digit code from the e-mail.`,
};

export const DEFAULT_WORKFLOW_ARTICLE_LABELS: WorkflowArticleLabels = {
  articleGroup: (title) => `Article: ${title}`,
  articleNewTab: "(opens in a new tab)",
};

export const DEFAULT_WORKFLOW_RATING_LABELS: WorkflowRatingLabels = {
  ratingGroup: "Your rating",
  rateStar: (stars) => `Rate ${String(stars)} out of 5`,
  starsOutOf: (stars) => `${String(stars)} out of 5`,
  thumbsUp: "Thumbs up",
  thumbsDown: "Thumbs down",
  ratingComment: "Anything to add? (optional)",
  ratingCommentCount: (used, max) => `${String(used)} of ${String(max)} characters`,
  ratingSubmit: "Submit",
  ratingSelected: (rating) => `Selected: ${rating}`,
};

export const DEFAULT_WORKFLOW_LABELS: WorkflowLabels = {
  choose: "Choose an option",
  chosen: "Your choice",
  submit: "Continue",
  skip: "Skip",
  select: "Choose…",
  required: "This field is required.",
  invalidEmail: "Enter a valid email address.",
  invalidPhone: "Enter a valid phone number, e.g. +63 917 123 4567.",
  invalidNumber: "Enter a number.",
  invalidDate: "Choose a valid date.",
  invalidOption: "Choose one of the options.",
  invalidFromServer: "That didn't look right. Please check it and try again.",
  expired: "This question has closed. Type your message to continue.",
  privacyLink: "Read the privacy notice",
  answeredWith: (value) => `You answered: ${value}`,
  typingLocked: "Choose an option above",
};

const DEFAULT_CARD_LABELS: ResolvedWorkflowLabels = { ...DEFAULT_WORKFLOW_LABELS, ...DEFAULT_WORKFLOW_RATING_LABELS, ...DEFAULT_WORKFLOW_REPLY_LABELS, ...DEFAULT_WORKFLOW_VERIFY_LABELS, ...DEFAULT_WORKFLOW_ARTICLE_LABELS };

export function resolveWorkflowLabels(overrides?: Partial<WorkflowCardLabels>): ResolvedWorkflowLabels {
  return overrides ? { ...DEFAULT_CARD_LABELS, ...overrides } : DEFAULT_CARD_LABELS;
}

/** The message shown for a refused value. */
export function workflowIssueText(labels: WorkflowLabels, issue: WorkflowValueIssue): string {
  switch (issue) {
    case "required":
      return labels.required;
    case "email":
      return labels.invalidEmail;
    case "phone":
      return labels.invalidPhone;
    case "number":
      return labels.invalidNumber;
    case "date":
      return labels.invalidDate;
    case "option":
      return labels.invalidOption;
  }
}

/** How the composer is set up to answer an open `collect_reply` card: the keyboard of its format, the question as the field's name, the placeholder, and the server's "ask again" note. */
export interface WorkflowReplyComposer extends Omit<WorkflowReplyInput, "question"> {
  label: string;
  placeholder: string;
  error?: string;
}

export function workflowReplyComposer(card: Pick<WorkflowStepPayload, "field" | "error"> & Partial<Pick<WorkflowStepPayload, "kind" | "otp">>, overrides?: Partial<WorkflowCardLabels>): WorkflowReplyComposer {
  const labels = resolveWorkflowLabels(overrides);
  const { question, ...traits } = workflowReplyInput(card);
  return {
    ...traits,
    label: labels.replyAnswer(question),
    placeholder: labels.replyPlaceholder,
    ...(card.error === "invalid" ? { error: labels.invalidFromServer } : {}),
  };
}
